// Test prep choices (docs/plans/ONBOARDING.md, phase 5). Safe for the client: metadata and plain logic
// only, no question engine.
//
// A test plan is saved in the existing `profile.testTargets` list, one string per choice:
//   "u4_slope_points"                a topic in the learner's current course (the original format)
//   "unit:Fractions as numbers"      a whole unit of the current course, one choice
//   "preset:sat-algebra"             a one-tap preset of the current course (SAT courses)
//   "grade4:g4_decimal_place"        a topic from another course on the learner's path
//   "grade4:unit:Decimals"           a whole unit from another course on the learner's path
// Profiles saved before this change hold only plain topic ids, so they keep their meaning.
import {COURSE_IDS, DEFAULT_TRACK, TRACKS, onAnyTrack, onTrack, trackOfCourse, type CourseId, type Track} from './course-catalog';

// A whole unit or a preset counts as one choice.
export const TEST_CHOICE_CAP = 40;
// How far ahead a test date may be set.
export const TEST_MAX_DAYS_AHEAD = 366;

export type ChoiceKind = 'topic' | 'unit' | 'preset';
export interface TestChoice {course: CourseId; kind: ChoiceKind; value: string}

const isCourse = (value: string): value is CourseId => (COURSE_IDS as readonly string[]).includes(value);

export function parseChoice(key: string, current: CourseId): TestChoice | null {
  if (!key || key.length > 160) return null;
  if (key.startsWith('unit:')) return key.length > 5 ? {course: current, kind: 'unit', value: key.slice(5)} : null;
  if (key.startsWith('preset:')) return key.length > 7 ? {course: current, kind: 'preset', value: key.slice(7)} : null;
  const colon = key.indexOf(':');
  if (colon < 0) return {course: current, kind: 'topic', value: key};
  const course = key.slice(0, colon), rest = key.slice(colon + 1);
  if (!isCourse(course) || !rest) return null;
  if (rest.startsWith('unit:')) return rest.length > 5 ? {course, kind: 'unit', value: rest.slice(5)} : null;
  if (rest.includes(':')) return null;
  return {course, kind: 'topic', value: rest};
}

// The key for a choice, written relative to the learner's current course, so a plan made in the
// current course reads exactly like the original format.
export function choiceKey(choice: TestChoice, current: CourseId): string {
  const body = choice.kind === 'topic' ? choice.value : `${choice.kind}:${choice.value}`;
  if (choice.course === current) return body;
  if (choice.kind === 'preset') throw new Error('Presets belong to the current course');
  return `${choice.course}:${body}`;
}

// Earlier courses whose topics a test can include, most recent first. Tests often review.
// These follow the child's learning track (course-catalog.ts TRACKS): on the track, every course before
// this one. Off the track, the track courses a learner would have come through: Prealgebra after Grade 7,
// the SAT courses after the track's third high-school year, the electives after the whole track.
// The legacy `early` course is never offered.
const OFF_TRACK: Partial<Record<CourseId, (list: readonly CourseId[]) => CourseId[]>> = {
  prealgebra: list => list.slice(0, list.indexOf('grade7') + 1),
  'sat-fundamentals': list => list.slice(0, list.indexOf('precalculus')),
  'sat-prep': list => [...list.slice(0, list.indexOf('precalculus')), 'sat-fundamentals'],
  'probability-statistics': list => [...list],
  'discrete-mathematics': list => [...list],
};
export function reviewCourses(course: CourseId, track?: Track): CourseId[] {
  const own = track && onTrack(course, track) ? track : onAnyTrack(course) ? trackOfCourse(course) : track ?? DEFAULT_TRACK;
  const list = TRACKS[own] as readonly CourseId[], i = list.indexOf(course);
  const earlier = i >= 0 ? list.slice(0, i) : OFF_TRACK[course]?.(list) ?? [];
  return earlier.filter(c => c !== course && c !== 'early').reverse();
}

// Whole days from `today` to `date` (both YYYY-MM-DD).
export function daysUntil(today: string, date: string): number {
  return Math.round((Date.parse(`${date}T12:00:00Z`) - Date.parse(`${today}T12:00:00Z`)) / 86400000);
}
export function addDaysIso(day: string, n: number): string {
  return new Date(Date.parse(`${day}T12:00:00Z`) + n * 86400000).toISOString().slice(0, 10);
}

// "Test today", "Test tomorrow", "Test in 5 days". Never a raw date.
export function countdownText(daysLeft: number): string {
  if (daysLeft < 0) return 'Test day has passed';
  if (daysLeft === 0) return 'Test today';
  if (daysLeft === 1) return 'Test tomorrow';
  return `Test in ${daysLeft} days`;
}

// "Thursday, October 22". The year appears only when it is not this year.
export function friendlyDate(date: string, today?: string): string {
  const value = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(value.getTime())) return '';
  const sameYear = !today || today.slice(0, 4) === date.slice(0, 4);
  return new Intl.DateTimeFormat('en-US', {weekday: 'long', month: 'long', day: 'numeric', ...(sameYear ? {} : {year: 'numeric'}), timeZone: 'UTC'}).format(value);
}

// "Fractions", "Fractions and Decimals", "Fractions, Decimals and 3 more".
export function coversText(labels: string[], shown = 3): string {
  if (!labels.length) return '';
  if (labels.length <= shown) return labels.length === 1 ? labels[0]! : `${labels.slice(0, -1).join(', ')} and ${labels.at(-1)}`;
  return `${labels.slice(0, shown).join(', ')} and ${labels.length - shown} more`;
}

// Picker state. A unit is either chosen whole (one key) or through some of its topics (one key each).
export type UnitPick = 'whole' | 'some' | 'none';
export function unitPick(selected: readonly string[], unitKey: string, topicKeys: readonly string[]): UnitPick {
  if (selected.includes(unitKey)) return 'whole';
  return topicKeys.some(k => selected.includes(k)) ? 'some' : 'none';
}
export const atCap = (selected: readonly string[]) => selected.length >= TEST_CHOICE_CAP;

// One tap chooses the whole unit (replacing any single topics from it) or clears it.
export function toggleUnit(selected: readonly string[], unitKey: string, topicKeys: readonly string[]): string[] {
  if (selected.includes(unitKey)) return selected.filter(k => k !== unitKey);
  const rest = selected.filter(k => !topicKeys.includes(k));
  return rest.length >= TEST_CHOICE_CAP ? [...selected] : [...rest, unitKey];
}

// Choosing single topics inside a unit. Unticking one topic of a whole unit keeps the others;
// ticking the last missing topic turns them back into the whole unit (one choice).
export function toggleTopic(selected: readonly string[], unitKey: string, topicKeys: readonly string[], topicKey: string): string[] {
  if (selected.includes(unitKey)) {
    const others = topicKeys.filter(k => k !== topicKey);
    const next = [...selected.filter(k => k !== unitKey), ...others];
    return next.length > TEST_CHOICE_CAP ? [...selected] : next;
  }
  if (selected.includes(topicKey)) return selected.filter(k => k !== topicKey);
  const next = [...selected, topicKey];
  if (topicKeys.length > 1 && topicKeys.every(k => next.includes(k))) return [...next.filter(k => !topicKeys.includes(k)), unitKey];
  return next.length > TEST_CHOICE_CAP ? [...selected] : next;
}

export function togglePreset(selected: readonly string[], presetKey: string): string[] {
  if (selected.includes(presetKey)) return selected.filter(k => k !== presetKey);
  return atCap(selected) ? [...selected] : [...selected, presetKey];
}

// What the test-prep API sends about a learner's plan.
export interface TestPlanView {
  date: string;
  daysLeft: number;
  // Topics behind the choices: in the current course (learned counts these) and from earlier courses.
  targets: number;
  learned: number;
  gaps: string[];
  review: number;
  choices: {key: string; label: string}[];
  setBy: 'child' | 'parent' | null;
}

// The picker's catalog for one course.
export interface TestCatalog {
  course: CourseId;
  current: CourseId;
  courses: {id: CourseId; label: string}[];
  units: {key: string; name: string; topics: {key: string; name: string}[]}[];
  presets: {key: string; name: string; description: string}[];
}
